/* eslint-disable @typescript-eslint/no-explicit-any */
import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  inject,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

import { DomManipulationService } from '@digital-platform/rs-shared/ui';
import { IconComponent } from '@digital-platform/rs-shared/ui-brand';
import { WindowService } from '../services/window.service';
import { YoutubeService } from '../services/youtube.service';

@Component({
  standalone: true,
  selector: 'digital-platform-youtube-video',
  templateUrl: './youtube-video.component.html',
  styleUrls: ['./youtube-video.component.scss'],
  providers: [NgbModal, DomManipulationService],
  imports: [IconComponent],
})
export class YoutubeVideoComponent implements OnInit, OnDestroy {
  @Input() public videoId = '';
  @Input() public autoplay = true;
  @Input() public showControls = true;
  @Input() public muted = false;
  @Input() public startTime = 0;
  @Input() public endTime = 0;

  @ViewChild('video', { static: true }) private video:
    | ElementRef<HTMLVideoElement>
    | undefined = undefined;

  public player!: {
    destroy: () => void;
  };
  public unsubscribe$ = new Subject<void>();

  private platformId = inject(PLATFORM_ID);

  constructor(
    @Inject(WindowService) private windowService: WindowService,
    private youtubeService: YoutubeService,
    // eslint-disable-next-line  @typescript-eslint/no-unused-vars
    private domManipulationService: DomManipulationService,
    private modalService: NgbModal
  ) {}

  public ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // lazy loading YouTube Library
    this.domManipulationService.addScriptTag(
      'https://www.youtube.com/iframe_api',
      'yt-video'
    );
    this.youtubeService.onYouTubeIframeAPIReady
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((loaded) => loaded === true)
      )
      .subscribe(() => this.onAPIReady());
  }

  public ngOnDestroy() {
    if (this.player) {
      this.player.destroy();
    }

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public closeModal() {
    this.modalService.dismissAll();
  }

  private onAPIReady() {
    this.player = new this.windowService.nativeWindow.YT.Player(
      (this.video as ElementRef<HTMLVideoElement>).nativeElement,
      {
        events: {
          onReady: () => {
            const videoTitle = (
              document.querySelector('.modal-content iframe') as HTMLElement
            ).getAttribute('title');
            (document.querySelector('#video-title') as HTMLElement).innerText =
              videoTitle as string;
            (
              document.querySelector('#mobile-video-title') as HTMLElement
            ).innerText = videoTitle as string;
          },
          onStateChange: (state: { data: Record<string | number, string> }) =>
            this.onStateChange(state.data),
        },
        playerVars: {
          autoplay: this.autoplay ? 1 : 0,
          controls: this.showControls ? 1 : 0,
          end: this.endTime,
          mute: this.muted ? 1 : 0,
          rel: 0,
          start: this.startTime,
        },
        videoId: this.videoId,
      }
    );
  }

  private onStateChange(state: Record<string | number, string>) {
    const playerState = this.windowService.nativeWindow.YT.PlayerState;
    if (state === playerState.PLAYING) {
      this.youtubeService.youtubePlay(this.player);
    } else if (state === playerState.PAUSED) {
      this.youtubeService.youtubePause(this.player);
    } else if (state === playerState.ENDED) {
      this.youtubeService.youtubeFinish(this.player);
    }
  }
}
